import api from './api';

/** آمار برداشت کاربران — آینهٔ userWithdrawalRequest.service.getWithdrawalStatistics (فقط درخواست‌های واقعی: PENDING فقط با OTP تأییدشده) */
export interface WalletStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  processingRequests: number;
  completedRequests: number;
  rejectedRequests: number;
  cancelledRequests: number;
  totalAmount: number;
  pendingAmount: number;
  /** APPROVED + PROCESSING — پولی که واقعاً از موجودی کاربر کنار گذاشته شده */
  reservedAmount: number;
  completedAmount: number;
  byStatus?: Record<string, { count: number; amount: number }>;
}

/** پاسخ GET /admin/wallet/user/:userId — خلاصهٔ کیف پول (مبالغ تومان) */
export interface UserWallet {
  userId: string;
  user?: { name?: string; phoneNumber?: string; email?: string };
  summary: {
    balance: number;
    lockedBalance: number;
    blockedForWithdrawal?: number;
    availableBalance: number;
    totalEarned: number;
    totalSpent: number;
    totalWithdrawn: number;
    recentTransactions?: any[];
    lastTransactionAt?: string | null;
  };
  recentTransactions?: any[];
  createdAt?: string | null;
  updatedAt?: string | null;
  isActive?: boolean;
}

export interface WalletSystemStats {
  totalUsers: number;
  totalWallets: number;
  activeWallets: number;
  inactiveWallets: number;
  walletsWithBalance: number;
  negativeWallets: number;
  totalBalance: number;
  lockedBalance: number;
  blockedForWithdrawal: number;
  availableBalance: number;
  averageBalance: number;
  totalEarned: number;
  totalSpent: number;
  recentTransactions: number;
  transactionStats?: Record<string, { count: number; amount: number }>;
  withdrawals?: WalletStats;
}

class WalletAdminService {
  private baseURL = '/admin/wallet';

  /**
   * Get wallet statistics
   */
  async getStatistics(): Promise<WalletStats> {
    const response = await api.get(`${this.baseURL}/statistics`);
    return (response as any)?.data || response;
  }

  /**
   * Get system-wide wallet statistics
   */
  async getSystemStatistics(): Promise<WalletSystemStats> {
    const response = await api.get(`${this.baseURL}/system-statistics`);
    return (response as any)?.data || response;
  }

  /**
   * Get user wallet details
   */
  async getUserWallet(userId: string): Promise<UserWallet> {
    const response = await api.get(`${this.baseURL}/user/${userId}`);
    return (response as any)?.data || response;
  }

  /**
   * Add bonus to user wallet
   */
  async addBonus(userId: string, amount: number, description?: string, idempotencyKey?: string): Promise<any> {
    const response = await api.post(`${this.baseURL}/add-bonus`, {
      userId,
      amount,
      description,
      idempotencyKey
    });
    return (response as any)?.data || response;
  }

  /**
   * Deduct from user wallet
   */
  async deductFromWallet(userId: string, amount: number, reason: string): Promise<any> {
    const response = await api.post(`${this.baseURL}/deduct`, {
      userId,
      amount,
      reason
    });
    return (response as any)?.data || response;
  }

  /**
   * Manual refund to user wallet
   */
  async manualRefund(userId: string, amount: number, reason: string, orderNumber?: string, idempotencyKey?: string): Promise<any> {
    const response = await api.post(`${this.baseURL}/manual-refund`, {
      userId,
      amount,
      reason,
      orderNumber: orderNumber || undefined,
      idempotencyKey
    });
    return (response as any)?.data || response;
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number): string {
    return amount?.toLocaleString('fa-IR') || '0';
  }
}

export const walletAdminService = new WalletAdminService();
export default walletAdminService;

